import { IPermissions } from 'types';

/**
 * Check if user has permission to manage tools.
 * Must have canManagePlatform or at least one manageToolPermissions.
 * @param permissions Permissions object from SPA Initial Data
 * @returns True if user has permission to manage tools
 */
const canManageTools = (permissions: IPermissions): boolean => {
  return (
    (permissions.canManagePlatform ||
      permissions.manageToolPermissions.length > 0) &&
    !permissions.hasLTI13PaicePermission
  );
};

/**
 * Check if user has permission to edit a tool.
 * @param permissions Permissions object from SPA Initial Data
 * @param toolId Id of tool being edited.
 * @returns True if user has permission to edit the tool.
 */
const canEditTool = (permissions: IPermissions, toolId: string): boolean => {
  if (permissions.canManagePlatform) {
    return true;
  }

  return (
    permissions.manageToolPermissions.indexOf(
      `LTI13_MANAGE_${toolId.toUpperCase()}`
    ) !== -1
  );
};

/**
 * Check if user has permission to create links.
 * Must have canCreateLinks and can manage a tool.
 * @param permissions Permissions object from SPA Initial Data
 * @returns True if user has permission to create links or has canManagePlatform permission.
 */
const canCreateLinks = (permissions: IPermissions): boolean => {
  if (permissions.canManagePlatform || permissions.hasLTI13PaicePermission) {
    return true;
  }
  return canManageTools(permissions) && permissions.canCreateLinks;
};

/**
 * Check if user has permission to modify links.
 * Must have canModifyLinks and can manage a tool.
 * @param permissions Permissions object from SPA Initial Data
 * @returns True if user has permission to modify links or has canManagePlatform permission.
 */
const canModifyLinks = (permissions: IPermissions): boolean => {
  if (permissions.canManagePlatform || permissions.hasLTI13PaicePermission) {
    return true;
  }
  return canManageTools(permissions) && permissions.canModifyLinks;
};

/**
 * Check if user has permission to view links.
 * Must have canReadLinks and can manage a tool.
 * @param permissions Permissions object from SPA Initial Data
 * @returns True if user has permission to view links or has canManagePlatform permission.
 */
const canViewLinks = (permissions: IPermissions): boolean => {
  if (permissions.canManagePlatform || permissions.hasLTI13PaicePermission) {
    return true;
  }
  return canManageTools(permissions) && permissions.canReadLinks;
};

export {
  canManageTools,
  canEditTool,
  canCreateLinks,
  canModifyLinks,
  canViewLinks,
};
